import { ClientError, GraphQLClient } from "graphql-request";

const apiUrl = process.env.NEXT_PUBLIC_BACKEND_URL + "/graphql";
const isDev = process.env.NODE_ENV === "development";

/**
 * GraphQL client that routes requests through /api/graphql for auth handling.
 * The server-side API route handles cookies and token refresh automatically.
 */
class GraphQLClientWithAuth {
  private endpointUrl: string;
  private dontSendAuthToken: boolean;
  private dontTriggerFlows: boolean;

  constructor(
    options: {
      dontSendAuthToken?: boolean;
      endpointName?: string;
      dontTriggerFlows?: boolean;
    } = {},
  ) {
    this.dontSendAuthToken = options.dontSendAuthToken ?? false;
    this.dontTriggerFlows = options.dontTriggerFlows ?? false;

    // For dev with named endpoints, construct the full URL
    this.endpointUrl = isDev && options.endpointName ? `${apiUrl}/${options.endpointName}` : apiUrl;
  }

  private buildHeaders(extra?: Record<string, string>): Record<string, string> {
    const headers: Record<string, string> = { "Content-Type": "application/json", ...extra };
    if (this.dontTriggerFlows) {
      headers["pylo-do-not-trigger-flows"] = "true";
    }
    return headers;
  }

  async request<T = unknown>(
    document: string,
    variables?: Record<string, unknown>,
    options?: { signal?: AbortSignal },
  ): Promise<T> {
    // For unauthenticated requests, use direct GraphQL client
    if (this.dontSendAuthToken) {
      const client = new GraphQLClient(this.endpointUrl, {
        next: { revalidate: 0 },
        mode: "cors",
        signal: options?.signal,
        headers: this.dontTriggerFlows ? { "pylo-do-not-trigger-flows": "true" } : undefined,
      });
      return client.request<T>(document, variables);
    }

    // For authenticated requests, route through /api/graphql
    // which handles cookies server-side
    const response = await fetch("/api/graphql", {
      method: "POST",
      headers: this.buildHeaders(),
      body: JSON.stringify({ query: document, variables }),
      signal: options?.signal,
    });

    if (response.status === 401) {
      // Redirect to login on auth errors, but NOT if already on auth pages
      if (typeof window !== "undefined") {
        const isOnAuthPage = window.location.pathname.includes("/auth/");
        if (!isOnAuthPage) {
          window.location.href = `/p/auth/login?redirect=${encodeURIComponent(window.location.pathname)}`;
        }
      }
      throw new ClientError({ status: 401, errors: [{ message: "Unauthorized" }] } as any, {
        query: document,
        variables,
      });
    }

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();

    if (result.errors) {
      throw new ClientError({ status: response.status, ...result } as any, {
        query: document,
        variables,
      });
    }

    return result.data as T;
  }
}

export const createGraphQLClient = ({
  dontSendAuthToken = false,
  endpointName,
  dontTriggerFlows = false,
}: {
  dontSendAuthToken?: boolean;
  endpointName?: string;
  dontTriggerFlows?: boolean;
} = {}) => {
  return new GraphQLClientWithAuth({
    dontSendAuthToken,
    endpointName,
    dontTriggerFlows,
  });
};
